import type { ApiEndpointQuery } from "@reduxjs/toolkit/query"
import type { GroupBase } from "react-select"
import type { AsyncCreatableProps } from "react-select/async-creatable"
import AsyncCreatableSelect from "react-select/async-creatable"
import type { ReactElement } from "react"
import { useCallback, useState } from "react"
import { useAppDispatch } from "../app/hooks"
import debounce from "lodash/debounce"

export type EntitySelectProps = {
  action: ApiEndpointQuery<any, any>
  payloadKey?: string | null
  getActionParams?: (searchTerm: string) => any
  onLoadSuccess?: (payload: any) => void
  onCreateOption?: (option: any) => void
}
const defaultGetActionParams = (searchTerm: string) => searchTerm

/**
 * This component wraps react-select component with all available properties. Documentation can be found
 * here https://react-select.com/props
 * @param props
 * @constructor
 */
export const EntitySelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: Partial<AsyncCreatableProps<Option, IsMulti, Group>> & EntitySelectProps,
): ReactElement => {
  const {
    action,
    payloadKey = "results",
    getActionParams = defaultGetActionParams,
    onLoadSuccess,
    ...restProps
  } = props
  const appDispatch = useAppDispatch()
  const [loading, setLoading] = useState<boolean>(false)

  const debounced = useCallback(
    debounce((resolve, searchTerm) => {
      setLoading(true)
      appDispatch(action.initiate(getActionParams(searchTerm)))
        .unwrap()
        .then((payload: any) => {
          setLoading(false)
          resolve(payloadKey ? payload[payloadKey] : payload)
          if (onLoadSuccess) {
            onLoadSuccess(payload)
          }
        })
        .catch((err: any) => {
          setLoading(false)
          console.error(err)
        })
    }, 1000),
    [action],
  )

  const loadSearchOptions = useCallback(
    (searchTerm: string) => {
      return new Promise<any[]>(resolve => {
        debounced(resolve, searchTerm)
      })
    },
    [debounced],
  )

  return (
    <AsyncCreatableSelect isLoading={loading} loadOptions={loadSearchOptions} isSearchable isClearable {...restProps} />
  )
}
